import {
  to,
  label,
  displayNumber,
  roundByMagnitude,
  getUnitsForQuantity,
  isNumeric,
} from '@oliasoft-open-source/units';
import { Tooltip } from '@oliasoft-open-source/react-ui-library';
import { isEmpty } from 'es-toolkit/compat';
import { TCommonTableCell } from 'common/interfaces/objects-interfaces';

export const createUnitsArray = (quantities: string[]) => quantities.map((quantity) => {
  const unitsForQuantity = getUnitsForQuantity(quantity) as string[];
  return unitsForQuantity;
});

const convertibleUnitsCreator = (arr: string[], id: string) => arr.map((item) => ({
  value: item,
  label: label(item),
  testId: `${id}-convertible-units-option-${item}`,
}));

const onConvertUnits = (
  columnIndex: number,
  fromUnit: string,
  toUnit: string,
  data: TCommonTableCell[][],
  setData: (data: TCommonTableCell[][]) => void,
) => {
  const newData = data.map((row) => {
    const newRow = [...row];
    const cellValue = row[columnIndex];
    const cellValueIsNumeric = (typeof cellValue === 'string' || typeof cellValue === 'number') && isNumeric(cellValue);
    if (cellValueIsNumeric) {
      newRow[columnIndex] = to(cellValue, fromUnit, toUnit);
      return newRow;
    }
    if (row[columnIndex] === '') {
      return newRow;
    }
    newRow[columnIndex] = toUnit;
    return newRow;
  });
  setData(newData);
};

const headerToTooltip = (header: string | number) => {
  const headerUpperCase = header.toString().toUpperCase();
  switch (headerUpperCase) {
    case 'DEPTH (TVD)':
    case 'TVD': return 'True Veritcal Depth';
    case 'DEPTH (MD)':
    case 'MD': return 'Measured Depth';
    case 'VERT.DEPTH': return 'Vertical Depth';
    case 'CL': return 'Curve Length';
    case 'INC': return 'Inclination';
    case 'AZI': return 'Azimuth';
    case 'EW': return 'East(+)/West(-) Relative Position';
    case 'NS': return 'North(+)/South(-) Relative Position';
    case 'V.SEC': return 'Vertical Relative Position';
    case 'DLS': return 'Dogleg Severity';
    case 'TF': return 'Tool Face Angle';
    case 'BUILD':
    case 'BR': return 'Build Rate';
    case 'TURN':
    case 'TR': return 'Turn Rate';
    case 'HS ERR': return 'High Side Error';
    default: return '';
  }
};

const createHeaderTooltips = (headers: (string | number)[]) => headers.map(headerToTooltip);

export const optionCell = (
  headerUnits: string[],
  setHeaderUnits: (units: string[]) => void,
  units: string[],
  columnIndex: number,
  testId: string,
  data: TCommonTableCell[][],
  setData: (data: TCommonTableCell[][]) => void,
  readOnly: boolean,
) => {
  const cell = {
    options: convertibleUnitsCreator(units, testId),
    value: {
      value: data?.[0]?.[columnIndex],
      label: label(data?.[0]?.[columnIndex] as string),
    },
    type: 'Select',
    searchable: false,
    disabled: readOnly,
    testId: `${testId}-${data?.[0]?.[columnIndex]}`,
    onChange: (ev: any) => {
      const fromUnit = headerUnits[columnIndex];
      const toUnit = ev.target.value;
      onConvertUnits(columnIndex, fromUnit as string, toUnit, data, setData);
      const newHeaderUnits = [...headerUnits];
      newHeaderUnits[columnIndex] = toUnit;
      setHeaderUnits(newHeaderUnits);
    },
  };
  return cell;
};

export const createHeaders = (
  headerLabels: (number | string)[],
  quantities: string[],
  headerUnits: string[],
  setHeaderUnits: (units: string[]) => void,
  data: TCommonTableCell[][],
  setData: (data: TCommonTableCell[][]) => void,
  testId: string,
  readOnly = false,
) => {
  const headerTooltips = createHeaderTooltips(headerLabels);

  const mainHeaders = headerLabels.map((label, index) => ({
    value: <Tooltip flex="0 1 auto" text={headerTooltips[index]}>{label}</Tooltip>,
  }));

  const altUnits = createUnitsArray(quantities);

  const convertibleUnitsCells = altUnits.map((units, index) => {
    if (!headerUnits[index] || headerUnits[index] === '') {
      return {
        value: null,
      };
    }
    if (isEmpty(units)) {
      return {
        value: null,
      };
    }
    if (units.length === 1) {
      return {
        value: units[0],
      };
    }
    return {
      ...optionCell(
        headerUnits,
        setHeaderUnits,
        units,
        index,
        `${testId}-option-cell`,
        data,
        setData,
        readOnly
      ),
      value: {
        label: label(headerUnits[index] as string),
        value: headerUnits[index],
      },
    };
  });

  const noUnits = headerUnits.length === 0;

  const headers = [
    {
      cells: mainHeaders,
    },
    ...(noUnits ? [] : [{
      cells: convertibleUnitsCells,
    }]),
  ];

  return headers;
};

export const dataToRows = (data: (string | number)[][]) => data.map((row) => ({
  cells: row.map((value) => ({
    value:
          isNumeric(value)
            ? displayNumber(roundByMagnitude(value))
            : value,
  })),
}));
